/**
 * Money Flow Scanner — Cross-sectional MoneyFlowScore (0–100)
 * Leaderboard + Risk-Adjusted Score + Signal filters + Ticker drill-down
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import html2canvas from 'html2canvas';
import {
  Box, Typography, Button, CircularProgress, Skeleton, alpha,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
  Snackbar, Alert, Chip, IconButton, Drawer, Divider,
  ToggleButton, ToggleButtonGroup, TextField, InputAdornment,
  Collapse,
} from '@mui/material';
import {
  Refresh, TrendingUp, ShowChart, Speed, Waves, BoltOutlined,
  Warning as WarningIcon, CheckCircle, Visibility, Shield,
  ArrowUpward, ArrowDownward, WaterDrop, Close, Search,
  FilterList, Assessment,
  Recommend, Schedule, ExpandMore, ExpandLess, Timeline, FileDownloadOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ReferenceLine, Area, AreaChart,
} from 'recharts';
import { moneyFlowAPI } from '../services/api';

// ── Helpers ──
const fmt = (n) => n ? n.toLocaleString('vi-VN', { maximumFractionDigits: 0 }) : '0';
const fmtPct = (n, d = 1) => n != null ? `${n >= 0 ? '+' : ''}${n.toFixed(d)}%` : '—';
const fmtScore = (n) => n != null ? n.toFixed(1) : '—';
const fmtBn = (n) => n != null ? `${(n / 1e9).toFixed(1)}B` : '—';
const localizeCopy = (value, lang) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value[lang] || value.vi || value.en || '';
};

const buildSetupDiagnosticItems = (row, t) => {
  const diagnostics = row?.setup_diagnostics || {};
  const trend = diagnostics.trend || {};
  const base = diagnostics.base || {};
  const hasConstructiveBase = base.constructive ?? row?.has_constructive_base;
  const hasActiveFlow = base.active_flow ?? row?.has_active_flow;
  const items = [];

  if (trend.confirmed != null) {
    items.push({
      key: 'trend',
      label: t('mf.diagnostics.trend'),
      value: trend.confirmed ? t('mf.diagnostics.confirmed') : t('mf.diagnostics.notConfirmed'),
      color: trend.confirmed ? '#059669' : '#f97316',
    });
  }

  if (hasConstructiveBase != null) {
    items.push({
      key: 'base',
      label: t('mf.diagnostics.base'),
      value: hasConstructiveBase ? t('mf.diagnostics.constructive') : t('mf.diagnostics.notConstructive'),
      color: hasConstructiveBase ? '#2563eb' : '#64748b',
    });
  }

  if (hasActiveFlow != null) {
    items.push({
      key: 'flow',
      label: t('mf.diagnostics.flow'),
      value: hasActiveFlow ? t('mf.diagnostics.active') : t('mf.diagnostics.inactive'),
      color: hasActiveFlow ? '#0f766e' : '#64748b',
    });
  }

  if (base.spring_test_pass != null) {
    items.push({
      key: 'spring',
      label: t('mf.diagnostics.spring'),
      value: base.spring_test_pass ? t('mf.diagnostics.passed') : t('mf.diagnostics.notYet'),
      color: base.spring_test_pass ? '#0891b2' : '#64748b',
    });
  }

  if (base.lps_ready != null) {
    items.push({
      key: 'lps',
      label: t('mf.diagnostics.lps'),
      value: base.lps_ready ? t('mf.diagnostics.ready') : t('mf.diagnostics.waiting'),
      color: base.lps_ready ? '#7c3aed' : '#64748b',
    });
  }

  if (base.absorption_score != null) {
    items.push({
      key: 'absorption',
      label: t('mf.diagnostics.absorption'),
      value: `${base.absorption_score.toFixed(0)}/100`,
      color: '#475569',
    });
  }

  if (trend.rs60 != null) {
    items.push({
      key: 'rs60',
      label: 'RS60',
      value: trend.rs60.toFixed(3),
      color: '#475569',
    });
  }

  return items;
};

const buildSetupDiagnosticSummary = (row, t) => {
  const items = buildSetupDiagnosticItems(row, t);
  return items.slice(0, 4).map((item) => `${item.label}: ${item.value}`).join(' | ');
};

const buildCompactChecklist = (row, t) => {
  const diagnostics = row?.setup_diagnostics || {};
  const trend = diagnostics.trend || {};
  const base = diagnostics.base || {};
  const parts = [];

  if (trend.confirmed != null) {
    parts.push(trend.confirmed ? t('mf.diagnostics.confirmedShort', 'Trend ok') : t('mf.diagnostics.missingShort', 'Trend yếu'));
  }
  if (base.constructive != null || row?.has_constructive_base != null) {
    parts.push((base.constructive ?? row?.has_constructive_base)
      ? t('mf.diagnostics.baseShort', 'Base tốt')
      : t('mf.diagnostics.baseWeakShort', 'Base yếu'));
  }
  if (base.active_flow != null || row?.has_active_flow != null) {
    parts.push((base.active_flow ?? row?.has_active_flow)
      ? t('mf.diagnostics.flowShort', 'Flow hoạt động')
      : t('mf.diagnostics.flowWeakShort', 'Flow yếu'));
  }

  return parts.join(' / ') || '—';
};

const getIntentDisplayTone = (flowIntent) => {
  switch (flowIntent) {
    case 'confirmed_entry':
      return { color: '#166534', bg: alpha('#22c55e', 0.16), border: alpha('#16a34a', 0.3), emphasis: 1 };
    case 'aggressive_entry':
      return { color: '#c2410c', bg: alpha('#fb923c', 0.14), border: alpha('#f97316', 0.28), emphasis: 0.94 };
    case 'constructive_accumulation':
      return { color: '#1d4ed8', bg: alpha('#60a5fa', 0.15), border: alpha('#3b82f6', 0.28), emphasis: 0.98 };
    case 'early_accumulation':
      return { color: '#0f766e', bg: alpha('#2dd4bf', 0.12), border: alpha('#14b8a6', 0.24), emphasis: 0.9 };
    case 'speculative_rebound':
      return { color: '#a16207', bg: alpha('#facc15', 0.1), border: alpha('#eab308', 0.22), emphasis: 0.78 };
    default:
      return { color: '#64748b', bg: alpha('#cbd5e1', 0.12), border: alpha('#94a3b8', 0.18), emphasis: 0.72 };
  }
};

const splitReadableReason = (text) => {
  if (!text || typeof text !== 'string') return { headline: '', points: [] };

  const normalized = text.replace(/\s+/g, ' ').trim();
  if (!normalized) return { headline: '', points: [] };

  const [headlinePart, ...restParts] = normalized
    .split('|')
    .map(part => part.trim())
    .filter(Boolean);

  const bodyText = restParts.join(' ');
  const points = bodyText
    .split(/(?<=\.)\s+/)
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => part.replace(/\.\s*$/, ''));

  return {
    headline: headlinePart || '',
    points,
  };
};

const buildDrawerSnapshotPayload = (ticker, style, row, companyName = null) => ({
  ticker,
  style,
  companyName: companyName || row?.company_name || row?.companyName || null,
  latestRecord: row,
});

const thSx = {
  fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8',
  letterSpacing: '0.02em', py: 0.8,
  borderBottom: '1px solid #e2e8f0', bgcolor: '#fafbfc',
};
const tdSx = { py: 0.7, fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' };

// Color helpers
const scoreColor = (s) => {
  if (s == null) return '#94a3b8';
  if (s >= 75) return '#10b981';
  if (s >= 50) return '#f59e0b';
  if (s >= 25) return '#f97316';
  return '#ef4444';
};

const heatColor = (level, score) => {
  if (level === 'overheated') return '#ef4444';
  if (level === 'hot') return '#f97316';
  if (level === 'warm') return '#f59e0b';
  if (level === 'cool') return '#10b981';
  if (score == null) return '#94a3b8';
  if (score >= 70) return '#ef4444';
  if (score >= 45) return '#f59e0b';
  return '#10b981';
};

const stretchColor = (s) => {
  if (s == null) return '#94a3b8';
  if (Math.abs(s) > 20) return '#ef4444';
  if (Math.abs(s) > 10) return '#f59e0b';
  return '#10b981';
};

const earlyColor = (s) => {
  if (s == null) return '#94a3b8';
  if (s >= 55) return '#a855f7';
  if (s >= 35) return '#6366f1';
  return '#94a3b8';
};
const toneColor = (tone, fallback = '#94a3b8') => ({
  positive: '#10b981',
  positive_strong: '#059669',
  info: '#3b82f6',
  accent: '#8b5cf6',
  neutral: '#94a3b8',
  muted: '#64748b',
  caution: '#f59e0b',
  warning: '#f97316',
  danger: '#ef4444',
}[tone] || fallback);

const metricSnapshot = (snapshot, fallback) => ({
  text: snapshot?.text,
  detail: snapshot?.detail,
  tone: snapshot?.tone,
  color: toneColor(snapshot?.tone, fallback.color),
  value: snapshot?.detail ? null : fallback.value,
  numericDetail: fallback.value,
});

// ── Stat Tile ──
const Tile = ({ label, value, sub, color, Icon }) => (
  <Box sx={{
    position: 'relative', overflow: 'hidden',
    borderRadius: 1.5, border: `1px solid ${alpha(color, 0.15)}`,
    bgcolor: '#fff', p: '14px 16px', transition: 'all 0.2s',
    '&:hover': { borderColor: alpha(color, 0.3), boxShadow: `0 4px 16px ${alpha(color, 0.08)}`, transform: 'translateY(-1px)' },
  }}>
    <Box sx={{ position: 'absolute', top: 0, right: 0, width: 64, height: 64, background: `radial-gradient(circle at top right, ${alpha(color, 0.06)} 0%, transparent 70%)` }} />
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{ width: 38, height: 38, borderRadius: 2, flexShrink: 0, background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 3px 8px ${alpha(color, 0.25)}` }}>
        <Icon sx={{ fontSize: 20, color: '#fff' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', lineHeight: 1, mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: '1.75rem', fontWeight: 900, color, lineHeight: 1.2, fontFamily: 'monospace' }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', mt: 0.4 }}>{sub}</Typography>}
      </Box>
    </Box>
  </Box>
);

// ── Semantic score badge ──
const ScoreBadge = ({ display, tooltipLabel }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
  const label = localizeCopy(display?.label, lang) || '—';
  const c = display?.color || '#94a3b8';
  const tooltip = tooltipLabel && display?.value != null
    ? `${tooltipLabel}: ${display.value.toFixed(1)}/100`
    : label;

  return (
    <Tooltip title={tooltip} arrow>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0,
          cursor: 'help',
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: '0.8rem',
            fontWeight: 800,
            letterSpacing: '-0.01em',
            color: c,
            lineHeight: 1.1,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ── Entry heat display ──
const fallbackHeatLabel = (value, t) => {
  if (value == null) return '';
  if (value >= 70) return t('mf.heat.overheated');
  if (value >= 45) return t('mf.heat.hot');
  if (value >= 20) return t('mf.heat.warm');
  return t('mf.heat.cool');
};
const EntryHeatBadge = ({ level, label, reason, score }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
  const text = localizeCopy(label, lang) || fallbackHeatLabel(score, t);
  const tooltip = localizeCopy(reason, lang);
  const color = heatColor(level, score);
  const content = (
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color }}>
      {text || '—'}
    </Typography>
  );

  if (!tooltip) return content;

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box component="span">{content}</Box>
    </Tooltip>
  );
};

const WatchlistTableSection = ({
  icon: Icon,
  accentColor,
  title,
  summary,
  rows,
  lang,
  style,
  openTickerDrawer,
  t,
}) => {
  if (!rows.length) return null;

  return (
    <Box sx={{ mb: 2.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <Box sx={{ px: 2.5, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap', mb: 0.6 }}>
          <Icon sx={{ fontSize: 16, color: accentColor }} />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#1e293b' }}>
            {title}
          </Typography>
          <Chip label={rows.length} size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha(accentColor, 0.08), color: accentColor }} />
        </Box>
        <Typography sx={{ fontSize: '0.68rem', color: '#64748b', lineHeight: 1.5 }}>
          {summary}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', mt: 1.1 }}>
          <Chip
            label={t('mf.legend.favorable', 'Vùng vào còn đẹp: có thể tiếp tục theo dõi sát')}
            size="small"
            sx={{ height: 22, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha('#10b981', 0.08), color: '#059669' }}
          />
          <Chip
            label={t('mf.legend.monitor', 'Canh điểm vào: setup đang tiến triển, chưa cần đuổi')}
            size="small"
            sx={{ height: 22, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha('#f59e0b', 0.08), color: '#d97706' }}
          />
          <Chip
            label={t('mf.legend.avoidChase', 'Tránh mua đuổi: đợi giá và rủi ro cân bằng lại')}
            size="small"
            sx={{ height: 22, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha('#ef4444', 0.08), color: '#dc2626' }}
          />
        </Box>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...thSx, width: 88 }}>{t('mf.table.status')}</TableCell>
              <TableCell sx={{ ...thSx, width: 86 }}>{t('mf.table.ticker')}</TableCell>
              <TableCell sx={thSx}>{t('mf.table.intent', 'Intent')}</TableCell>
              <TableCell sx={thSx}>{t('mf.table.checklist', 'Checklist')}</TableCell>
              <TableCell sx={{ ...thSx, width: 74, textAlign: 'center' }}>RS60</TableCell>
              <TableCell sx={{ ...thSx, width: 92, textAlign: 'center' }}>{t('mf.diagnostics.absorption')}</TableCell>
              <TableCell sx={{ ...thSx, width: 88 }}>{t('mf.table.entryHeat')}</TableCell>
              <TableCell sx={{ ...thSx, width: 84 }}>{t('mf.table.flowScore')}</TableCell>
              <TableCell sx={{ ...thSx, width: 92 }}>{t('mf.conviction.label')}</TableCell>
              <TableCell sx={thSx}>{t('mf.table.notes', 'Notes')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const blockers = row.flow_blockers || [];
              const intentLabel = localizeCopy(row.flow_intent_brief, lang) || localizeCopy(row.flow_intent_label, lang) || row.flow_intent;
              const intentSummary = localizeCopy(row.flow_intent_summary, lang) || row._statusTip;
              const intentTone = getIntentDisplayTone(row.flow_intent);
              const checklistSummary = buildCompactChecklist(row, t);
              const rs60Display = row?.setup_diagnostics?.trend?.rs60_display || row?.rs60_display || null;
              const absorptionDisplay = row?.setup_diagnostics?.base?.absorption_display || row?.absorption_display || null;
              const StatusIcon = row._statusIcon;

              return (
                <TableRow
                  key={`${title}-${row.ticker}`}
                  hover
                  onClick={() => openTickerDrawer(
                    row.ticker,
                    buildDrawerSnapshotPayload(row.ticker, style, row, row.company_name || row.companyName || null),
                    row.company_name || row.companyName || null,
                  )}
                  sx={{
                    cursor: 'pointer',
                    borderLeft: 3,
                    borderLeftColor: accentColor,
                    '&:hover': { bgcolor: alpha(accentColor, 0.04) },
                  }}
                >
                  <TableCell sx={tdSx}>
                    <Chip
                      icon={<StatusIcon sx={{ fontSize: '13px !important', color: `${row._statusColor} !important` }} />}
                      label={row._status}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        bgcolor: alpha(row._statusColor, 0.08),
                        color: row._statusColor,
                        border: `1px solid ${alpha(row._statusColor, 0.2)}`,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#0f172a' }}>{row.ticker}</Typography>
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Tooltip title={intentSummary} arrow placement="top-start">
                      <Box component="span">
                        <Chip
                          label={intentLabel}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.66rem',
                            fontWeight: 800,
                            color: intentTone.color,
                            bgcolor: intentTone.bg,
                            border: `1px solid ${intentTone.border}`,
                            opacity: intentTone.emphasis,
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontSize: '0.64rem', color: '#64748b', lineHeight: 1.5 }}>
                      {checklistSummary}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                    <Tooltip title={row?.rs60 != null ? `RS60: ${row.rs60.toFixed(3)}` : 'RS60'} arrow>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: rs60Display?.color || '#475569', lineHeight: 1.35 }}>
                        {localizeCopy(rs60Display?.label, lang) || '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                    <Tooltip title={row?.absorption_score != null ? `${t('mf.diagnostics.absorption')}: ${row.absorption_score.toFixed(0)}/100` : t('mf.diagnostics.absorption')} arrow>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: absorptionDisplay?.color || '#475569', lineHeight: 1.35 }}>
                        {localizeCopy(absorptionDisplay?.label, lang) || '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <EntryHeatBadge
                      level={row.entry_heat_level}
                      label={row.entry_heat_label}
                      reason={row.entry_heat_reason}
                      score={row.entry_heat_score ?? row.entry_risk}
                    />
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <ScoreBadge display={row.mf_score_display} tooltipLabel={t('mf.table.flowScore')} />
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <ScoreBadge display={row.conviction_display} tooltipLabel={t('mf.conviction.label')} />
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap' }}>
                      {blockers.length > 0 ? blockers.slice(0, 2).map((blocker) => (
                        <Chip
                          key={`${row.ticker}-${blocker.key}`}
                          label={localizeCopy(blocker.label, lang) || blocker.key}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.56rem',
                            bgcolor: '#f8fafc',
                            color: '#64748b',
                            border: '1px solid #e2e8f0',
                          }}
                        />
                      )) : (
                        <Typography sx={{ fontSize: '0.64rem', color: '#cbd5e1' }}>—</Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

WatchlistTableSection.propTypes = {
  icon: PropTypes.elementType.isRequired,
  accentColor: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  summary: PropTypes.string,
  rows: PropTypes.arrayOf(PropTypes.shape({
    ticker: PropTypes.string.isRequired,
    company_name: PropTypes.string,
    companyName: PropTypes.string,
    flow_intent: PropTypes.string,
    flow_intent_brief: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    flow_intent_label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    flow_intent_summary: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    flow_blockers: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    })),
    _status: PropTypes.string,
    _statusColor: PropTypes.string,
    _statusIcon: PropTypes.elementType,
    entry_heat_level: PropTypes.string,
    entry_heat_label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    entry_heat_reason: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    entry_heat_score: PropTypes.number,
    entry_risk: PropTypes.number,
    mf_score_display: PropTypes.object,
    conviction_display: PropTypes.object,
    setup_diagnostics: PropTypes.object,
    has_constructive_base: PropTypes.bool,
    has_active_flow: PropTypes.bool,
  })).isRequired,
  lang: PropTypes.string.isRequired,
  style: PropTypes.string.isRequired,
  openTickerDrawer: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

WatchlistTableSection.defaultProps = {
  summary: '',
};


// ── Empty State ──
const EmptyState = ({ Icon, title, subtitle }) => (
  <Box sx={{ py: 5, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#f8fafc', border: '1.5px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {Icon && <Icon sx={{ fontSize: 24, color: '#cbd5e1' }} />}
    </Box>
    <Box sx={{ textAlign: 'center' }}>
      <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#94a3b8', lineHeight: 1.4 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1', mt: 0.3, maxWidth: 400 }}>{subtitle}</Typography>}
    </Box>
  </Box>
);

// ══════════════════════════════════════════════════════════
// SIGNAL FILTER TABS
// ══════════════════════════════════════════════════════════
const SIGNAL_FILTERS = [
  { key: 'all',                 labelKey: 'mf.filter.all',                color: '#64748b', icon: FilterList },
  { key: 'buy_safe',            labelKey: 'mf.filter.safeBuy',            color: '#10b981', icon: CheckCircle },
  { key: 'buy_aggressive',      labelKey: 'mf.filter.aggressive',         color: '#f59e0b', icon: TrendingUp },
  { key: 'watch_accumulating',  labelKey: 'mf.filter.accumulating',       color: '#8b5cf6', icon: Visibility },
  { key: 'silent_accumulation', labelKey: 'mf.filter.silentAccum',        color: '#06b6d4', icon: Waves },
  { key: 'pre_breakout',        labelKey: 'mf.filter.preBreakout',        color: '#f97316', icon: Schedule },
  { key: 'emerging',            labelKey: 'mf.filter.emerging',           color: '#a855f7', icon: Speed },
  { key: 'watch',               labelKey: 'mf.filter.watch',              color: '#3b82f6', icon: Visibility },
  { key: 'risk',                labelKey: 'mf.filter.risk',               color: '#ef4444', icon: WarningIcon },
];

// Signal tier helpers
const _isTier = (row, tier) => {
  const st = row.signal_tier;
  if (st) return st === tier;
  // fallback to boolean fields
  if (tier === 'buy_safe') return row.Signal_BuyBias_Safe || row.signal_buy_bias_safe;
  if (tier === 'buy_aggressive') return row.Signal_BuyBias_Aggressive || row.signal_buy_bias_aggressive;
  if (tier === 'watch_accumulating') return row.Signal_Watch_Accumulating || row.signal_watch_accumulating;
  if (tier === 'silent_accumulation') return row.Signal_Silent_Accumulation || row.signal_silent_accumulation;
  if (tier === 'pre_breakout') return row.Signal_Pre_Breakout || row.signal_pre_breakout;
  if (tier === 'emerging') return row.Signal_Emerging || row.signal_emerging;
  if (tier === 'watch') return (row.Signal_Watch || row.signal_watch) && !row.Signal_BuyBias_Safe && !row.Signal_BuyBias_Aggressive;
  if (tier === 'risk') return row.Signal_Risk || row.signal_risk;
  return false;
};

const TIER_CONFIG = {
  buy_safe:            { labelKey: 'mf.tierLabel.buy_safe',            color: '#10b981' },
  buy_aggressive:      { labelKey: 'mf.tierLabel.buy_aggressive',      color: '#f59e0b' },
  watch_accumulating:  { labelKey: 'mf.tierLabel.watch_accumulating',  color: '#8b5cf6' },
  silent_accumulation: { labelKey: 'mf.tierLabel.silent_accumulation', color: '#06b6d4' },
  pre_breakout:        { labelKey: 'mf.tierLabel.pre_breakout',        color: '#f97316' },
  emerging:            { labelKey: 'mf.tierLabel.emerging',            color: '#a855f7' },
  watch:               { labelKey: 'mf.tierLabel.watch',               color: '#3b82f6' },
  risk:                { labelKey: 'mf.tierLabel.risk',                color: '#ef4444' },
  neutral:             { labelKey: 'mf.tierLabel.neutral',             color: '#94a3b8' },
};

// ══════════════════════════════════════════════════════════
// TICKER DETAIL DRAWER
// ══════════════════════════════════════════════════════════
const TickerDrawer = ({ open, onClose, ticker, style = 'swing', initialData = null }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportingImage, setExportingImage] = useState(false);
  const drawerCaptureRef = useRef(null);
  const drawerScrollRef = useRef(null);

  useEffect(() => {
    if (!open || !ticker) return;
    if (initialData?.ticker === ticker && initialData?.style === style && Array.isArray(initialData?.data)) {
      setData(initialData);
      setLoading(false);
      return;
    }

    if (initialData?.ticker === ticker && initialData?.style === style && initialData?.latestRecord) {
      const seededLatest = {
        ...initialData.latestRecord,
        company_name:
          initialData.latestRecord.company_name ||
          initialData.latestRecord.companyName ||
          initialData.companyName ||
          initialData.company_name ||
          null,
      };
      setData({
        ticker,
        style,
        companyName: initialData.companyName || initialData.company_name || seededLatest.company_name || null,
        data: [seededLatest],
      });
    }

    setLoading(true);
    moneyFlowAPI.getTicker(ticker, { style })
      .then(r => {
        const fetched = r.data;

        if (initialData?.ticker === ticker && initialData?.style === style && initialData?.latestRecord) {
          const seededLatest = {
            ...initialData.latestRecord,
            company_name:
              initialData.latestRecord.company_name ||
              initialData.latestRecord.companyName ||
              fetched?.companyName ||
              fetched?.company_name ||
              null,
          };
          const history = Array.isArray(fetched?.data) ? [...fetched.data] : [];
          const targetIdx = seededLatest?.date
            ? history.findIndex(item => item?.date === seededLatest.date)
            : (history.length > 0 ? history.length - 1 : -1);

          if (targetIdx >= 0) {
            history[targetIdx] = { ...history[targetIdx], ...seededLatest };
          } else {
            history.push(seededLatest);
          }

          history.sort((a, b) => String(a?.date || '').localeCompare(String(b?.date || '')));

          setData({
            ...fetched,
            companyName: fetched?.companyName || fetched?.company_name || seededLatest.company_name || null,
            data: history,
          });
          return;
        }

        setData(fetched);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [initialData, open, style, ticker]);

  const history = data?.data || [];
  const chartData = history.map(d => ({
    date: d.date?.slice(5),
    score: d.score,
    heat: d.entry_heat_score ?? d.risk_score,
    adjusted: d.adjusted_score,
  }));

  const latest = history.length > 0 ? history[history.length - 1] : null;
  const companyName =
    latest?.company_name ||
    latest?.companyName ||
    data?.company_name ||
    data?.companyName ||
    initialData?.company_name ||
    initialData?.companyName ||
    ticker;
  const currentPrice = latest?.close;
  const entryHeatReasonText = localizeCopy(latest?.entry_heat_reason, lang);
  const entryHeatReason = splitReadableReason(entryHeatReasonText);
  const handleExportDrawerImage = useCallback(async () => {
    if (!drawerCaptureRef.current || !drawerScrollRef.current || !latest || exportingImage) {
      return;
    }

    const captureNode = drawerCaptureRef.current;
    const scrollNode = drawerScrollRef.current;
    const paperNode = captureNode.parentElement;
    const previousOverflow = scrollNode.style.overflowY;
    const previousHeight = scrollNode.style.height;
    const previousMaxHeight = scrollNode.style.maxHeight;
    const previousFlex = scrollNode.style.flex;
    const previousCaptureHeight = captureNode.style.height;
    const previousPaperHeight = paperNode?.style.height || '';
    const previousPaperMaxHeight = paperNode?.style.maxHeight || '';
    const previousPaperOverflow = paperNode?.style.overflow || '';
    const previousPaperOverflowY = paperNode?.style.overflowY || '';

    try {
      setExportingImage(true);
      if (paperNode) {
        paperNode.style.height = 'auto';
        paperNode.style.maxHeight = 'none';
        paperNode.style.overflow = 'visible';
        paperNode.style.overflowY = 'visible';
      }
      scrollNode.style.overflowY = 'visible';
      scrollNode.style.height = 'auto';
      scrollNode.style.maxHeight = 'none';
      scrollNode.style.flex = '0 0 auto';
      captureNode.style.height = `${captureNode.scrollHeight}px`;

      await new Promise((resolve) => window.requestAnimationFrame(resolve));

      const canvas = await html2canvas(captureNode, {
        backgroundColor: '#fafbfc',
        scale: 2,
        useCORS: true,
        width: captureNode.scrollWidth,
        height: captureNode.scrollHeight,
        windowWidth: captureNode.scrollWidth,
        windowHeight: captureNode.scrollHeight,
        scrollX: 0,
        scrollY: 0,
      });

      const link = document.createElement('a');
      const asOfDate = latest?.date || new Date().toISOString().slice(0, 10);
      link.download = `${ticker || 'money-flow'}-drawer-${asOfDate}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Failed to export money flow drawer image', error);
    } finally {
      scrollNode.style.overflowY = previousOverflow;
      scrollNode.style.height = previousHeight;
      scrollNode.style.maxHeight = previousMaxHeight;
      scrollNode.style.flex = previousFlex;
      captureNode.style.height = previousCaptureHeight;
      if (paperNode) {
        paperNode.style.height = previousPaperHeight;
        paperNode.style.maxHeight = previousPaperMaxHeight;
        paperNode.style.overflow = previousPaperOverflow;
        paperNode.style.overflowY = previousPaperOverflowY;
      }
      setExportingImage(false);
    }
  }, [exportingImage, latest, ticker]);

  const tradePlan = latest ? [
    {
      label: t('mf.table.entry'),
      value: latest.entry_break_level ? fmt(latest.entry_break_level) : '—',
      hint: latest.close && latest.entry_break_level ? fmtPct(((latest.entry_break_level - latest.close) / latest.close) * 100) : null,
      color: '#3b82f6',
    },
    {
      label: t('mf.table.stopLoss'),
      value: latest.stop_loss_level ? fmt(latest.stop_loss_level) : '—',
      hint: latest.downside_pct ? `-${latest.downside_pct.toFixed(1)}%` : null,
      color: '#ef4444',
    },
    {
      label: t('mf.table.targetPrice'),
      value: latest.target_price ? fmt(latest.target_price) : '—',
      hint: latest.upside_pct ? `+${latest.upside_pct.toFixed(1)}%` : null,
      color: '#10b981',
    },
    {
      label: t('mf.table.rr'),
      value: latest.risk_reward ? `${latest.risk_reward.toFixed(1)}x` : '—',
      hint: latest.target_source ? latest.target_source.toUpperCase() : null,
      color: latest.risk_reward >= 2 ? '#10b981' : latest.risk_reward >= 1.3 ? '#f59e0b' : '#94a3b8',
    },
  ] : [];

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 440, md: 480 }, bgcolor: '#fafbfc' } }}
    >
      <Box ref={drawerCaptureRef} sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: '#fafbfc' }}>
      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25 }}>
        {latest ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', flexShrink: 0 }}>
              <Typography sx={{ fontWeight: 900, color: '#fff', fontSize: '0.85rem' }}>{ticker}</Typography>
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '0.97rem', sm: '1.02rem' },
                    color: '#1e293b',
                    lineHeight: 1.22,
                    letterSpacing: '-0.01em',
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    pr: 0.5,
                  }}
                >
                  {companyName}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap', mt: 0.3 }}>
                {currentPrice != null && (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.36, px: 0.75, py: 0.12, minHeight: 24, borderRadius: 999, bgcolor: '#eff6ff', border: '1px solid #dbeafe', boxSizing: 'border-box' }}>
                    <Typography sx={{ fontSize: '0.54rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1 }}>
                      {t('mf.table.price')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.76rem', color: '#2563eb', fontWeight: 800, fontFamily: 'monospace', lineHeight: 1 }}>
                      {fmt(currentPrice)}
                    </Typography>
                  </Box>
                )}
                {data?.style && (
                  <Typography sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: 24, px: 0.75, py: 0.12, borderRadius: 999, boxSizing: 'border-box', fontSize: '0.56rem', fontWeight: 700, color: '#7c3aed', bgcolor: alpha('#7c3aed', 0.08), lineHeight: 1 }}>
                    {data.style}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <Typography sx={{ fontWeight: 900, fontSize: '1.15rem' }}>{ticker}</Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }} data-html2canvas-ignore="true">
          <Tooltip title={exportingImage ? t('mf.drawer.exportingImage', 'Đang xuất ảnh...') : t('mf.drawer.exportImage', 'Xuất ảnh')} arrow>
            <span>
              <IconButton
                onClick={handleExportDrawerImage}
                size="small"
                disabled={!latest || loading || exportingImage}
                sx={{
                  border: '1px solid #e2e8f0',
                  bgcolor: '#fff',
                  color: '#475569',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                  '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
                }}
              >
                {exportingImage ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlined sx={{ fontSize: 18 }} />}
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              border: '1px solid #e2e8f0',
              bgcolor: '#fff',
              color: '#475569',
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
            }}
          >
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
      <Divider />

      <Box ref={drawerScrollRef} sx={{ overflowY: 'auto', flex: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, p: 2.5 }}>
            {/* skeleton score cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5 }}>
              {[0,1,2,3].map(i => (
                <Box key={i} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
                  <Skeleton variant="text" width="55%" height={12} sx={{ mb: 0.8 }} />
                  <Skeleton variant="text" width="50%" height={32} />
                </Box>
              ))}
            </Box>
            {/* skeleton risk badges */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[0,1,2,3,4].map(i => (
                <Skeleton key={i} variant="rounded" width={100} height={28} sx={{ borderRadius: 3 }} />
              ))}
            </Box>
            {/* skeleton chart */}
            <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2 }}>
              <Skeleton variant="text" width={120} height={16} sx={{ mb: 1.5 }} />
              <Skeleton variant="rounded" width="100%" height={200} sx={{ borderRadius: 1 }} />
            </Box>
            {/* skeleton component bars */}
            <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2 }}>
              <Skeleton variant="text" width={150} height={16} sx={{ mb: 1.5 }} />
              {[0,1,2,3,4,5].map(i => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Skeleton variant="text" width={80} height={14} />
                  <Skeleton variant="rounded" width="60%" height={8} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="text" width={30} height={14} />
                </Box>
              ))}
            </Box>
          </Box>
        ) : !latest ? (
          <EmptyState Icon={ShowChart} title={t('mf.drawer.noData')} />
        ) : (
          <Box>
            {/* ── Key Metrics (compact 2-row grid) ── */}
            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {(latest.date || entryHeatReasonText) && (
              <Box
                sx={{
                  borderRadius: 1.75,
                  bgcolor: alpha(heatColor(latest.entry_heat_level, latest.entry_heat_score), 0.08),
                  border: `1px solid ${alpha(heatColor(latest.entry_heat_level, latest.entry_heat_score), 0.16)}`,
                  px: 1.25,
                  py: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: (entryHeatReason.headline || entryHeatReason.points.length > 0) ? 0.5 : 0 }}>
                  {latest.date && (
                    <Typography
                      sx={{
                        fontSize: '0.66rem',
                        color: '#64748b',
                        fontWeight: 800,
                        fontFamily: 'monospace',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {latest.date}
                    </Typography>
                  )}
                  {latest.entry_heat_label && (
                    <Chip
                      label={localizeCopy(latest.entry_heat_label, lang)}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.58rem',
                        fontWeight: 700,
                        bgcolor: alpha(heatColor(latest.entry_heat_level, latest.entry_heat_score), 0.1),
                        color: heatColor(latest.entry_heat_level, latest.entry_heat_score),
                      }}
                    />
                  )}
                  {latest.score != null && (
                    <Tooltip title={`${t('mf.metrics.mfScore')}: ${fmtScore(latest.score)}/100`} arrow>
                      <Chip
                        label={localizeCopy(latest.metric_snapshot_view?.mf_score?.text, lang) || t('mf.metrics.mfScore')}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.58rem',
                          fontWeight: 700,
                          bgcolor: alpha(scoreColor(latest.score), 0.08),
                          color: scoreColor(latest.score),
                          cursor: 'help',
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
                {entryHeatReason.headline && (
                  <Typography sx={{ fontSize: '0.72rem', color: '#334155', fontWeight: 700, lineHeight: 1.4, mb: entryHeatReason.points.length ? 0.65 : 0 }}>
                    {entryHeatReason.headline}
                  </Typography>
                )}
                {entryHeatReason.points.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.45 }}>
                    {entryHeatReason.points.map((point, idx) => (
                      <Box key={`top-${idx}-${point.slice(0, 24)}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.65 }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: heatColor(latest.entry_heat_level, latest.entry_heat_score), mt: '0.42rem', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.68rem', color: '#64748b', lineHeight: 1.45 }}>
                          {point}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  entryHeatReasonText && (
                    <Typography sx={{ fontSize: '0.68rem', color: '#64748b', lineHeight: 1.45 }}>
                      {entryHeatReasonText}
                    </Typography>
                  )
                )}
              </Box>
            )}
            {tradePlan.some(item => item.value !== '—') && (
              <Box sx={{ borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <Box sx={{ px: 1.5, py: 0.8, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>
                    {t('mf.drawer.tradePlan')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1px', bgcolor: '#e2e8f0' }}>
                  {tradePlan.map(({ label: lbl, value: val, hint, color: c }) => (
                    <Box key={lbl} sx={{ bgcolor: '#fff', px: 1.5, py: 1 }}>
                      <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{lbl}</Typography>
                      <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, color: c, fontFamily: 'monospace', lineHeight: 1.3 }}>{val}</Typography>
                      {hint && (
                        <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', mt: 0.1 }}>
                          {hint}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1px', bgcolor: '#e2e8f0', borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              {[
                {
                  label: t('mf.metrics.mfScore'),
                  ...metricSnapshot(latest.metric_snapshot_view?.mf_score, { value: fmtScore(latest.score), color: scoreColor(latest.score) }),
                },
                {
                  label: t('mf.metrics.entryHeat'),
                  ...metricSnapshot(latest.metric_snapshot_view?.entry_heat, { value: fmtScore(latest.entry_heat_score ?? latest.risk_score), color: heatColor(latest.entry_heat_level, latest.entry_heat_score ?? latest.risk_score) }),
                },
                {
                  label: t('mf.metrics.adjusted'),
                  ...metricSnapshot(latest.metric_snapshot_view?.adjusted_score, { value: fmtScore(latest.adjusted_score), color: scoreColor(latest.adjusted_score) }),
                },
                {
                  label: 'RSI',
                  ...metricSnapshot(latest.metric_snapshot_view?.rsi14, { value: fmtScore(latest.rsi14), color: latest.rsi14 > 70 ? '#ef4444' : latest.rsi14 < 30 ? '#10b981' : '#3b82f6' }),
                },
                {
                  label: t('mf.metrics.stretchMa20'),
                  ...metricSnapshot(latest.metric_snapshot_view?.stretch_ma20, { value: fmtPct(latest.stretch_ma20), color: stretchColor(latest.stretch_ma20) }),
                },
                {
                  label: t('mf.metrics.stretchMa50'),
                  ...metricSnapshot(latest.metric_snapshot_view?.stretch_ma50, { value: fmtPct(latest.stretch_ma50), color: stretchColor(latest.stretch_ma50) }),
                },
                {
                  label: t('mf.metrics.consecutiveUp'),
                  ...metricSnapshot(latest.metric_snapshot_view?.consecutive_up, { value: latest.consecutive_up != null ? latest.consecutive_up.toString() : '—', color: latest.consecutive_up >= 5 ? '#ef4444' : latest.consecutive_up >= 3 ? '#f59e0b' : '#10b981' }),
                },
                {
                  label: t('mf.metrics.earlyScore'),
                  ...metricSnapshot(latest.metric_snapshot_view?.early_signal_score, { value: fmtScore(latest.early_signal_score ?? latest.EarlySignalScore), color: earlyColor(latest.early_signal_score ?? latest.EarlySignalScore) }),
                },
              ].map(({ label: lbl, text, detail, value: val, color: c }) => (
                <Box key={lbl} sx={{ bgcolor: '#fff', px: 1.5, py: 1 }}>
                  <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{lbl}</Typography>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: c, lineHeight: 1.35, mt: 0.2 }}>
                    {localizeCopy(text, lang) || val}
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', mt: 0.15, fontFamily: text ? 'monospace' : 'inherit' }}>
                    {localizeCopy(detail, lang) || (text ? val : '')}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* ── Early Signals chips (only if any active) ── */}
            {(latest.bb_squeeze || latest.vol_spring || latest.bullish_div || latest.roc_divergence || latest.flow_accel >= 1.0 || latest.score_velocity) && (
              <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', alignItems: 'center' }}>
                <BoltOutlined sx={{ fontSize: 14, color: '#f59e0b', mr: -0.3 }} />
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', mr: 0.5 }}>
                  {t('mf.drawer.signals')}
                </Typography>
                {latest.flow_accel >= 1.0 && <Chip label={t('mf.signal.flowAccel', { value: latest.flow_accel.toFixed(1) })} size="small" sx={{ fontSize: '0.58rem', height: 20, bgcolor: alpha('#a855f7', 0.1), color: '#a855f7', fontWeight: 700 }} />}
                {latest.score_velocity != null && <Chip label={`${latest.score_velocity >= 0 ? '+' : ''}${latest.score_velocity.toFixed(1)}/d`} size="small" sx={{ fontSize: '0.58rem', height: 20, bgcolor: alpha(latest.score_velocity >= 0 ? '#10b981' : '#ef4444', 0.1), color: latest.score_velocity >= 0 ? '#10b981' : '#ef4444', fontWeight: 700 }} />}
                {latest.bb_squeeze && <Chip label={t('mf.signal.bbSqueeze')} size="small" sx={{ fontSize: '0.58rem', height: 20, bgcolor: alpha('#a855f7', 0.1), color: '#a855f7', fontWeight: 700 }} />}
                {latest.vol_spring && <Chip label={t('mf.signal.volSpring')} size="small" sx={{ fontSize: '0.58rem', height: 20, bgcolor: alpha('#06b6d4', 0.1), color: '#06b6d4', fontWeight: 700 }} />}
                {latest.bullish_div && <Chip label={t('mf.drawer.bullishDiv')} size="small" sx={{ fontSize: '0.58rem', height: 20, bgcolor: alpha('#10b981', 0.1), color: '#10b981', fontWeight: 700 }} />}
                {latest.roc_divergence && <Chip label={t('mf.signal.rocDiv')} size="small" sx={{ fontSize: '0.58rem', height: 20, bgcolor: alpha('#3b82f6', 0.1), color: '#3b82f6', fontWeight: 700 }} />}
              </Box>
            )}

            {/* ── Component Breakdown (dense) ── */}
            {Array.isArray(latest?.component_breakdown_view) && latest.component_breakdown_view.length > 0 && (() => {
              const items = latest.component_breakdown_view;
              return items.length > 0 ? (
                <Box sx={{ borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <Box sx={{ px: 1.5, py: 0.8, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>
                      {t('mf.drawer.breakdown')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {items.map(({ key, label, text, tone }) => (
                      <Box key={key} sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, px: 1.5, py: 0.9, borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 'none' } }}>
                        <Typography sx={{ fontSize: '0.72rem', color: '#475569', fontWeight: 600, lineHeight: 1.35 }}>
                          {localizeCopy(label, lang) || '—'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: toneColor(tone, '#64748b'), textAlign: 'right', lineHeight: 1.35, flexShrink: 0 }}>
                          {localizeCopy(text, lang) || '—'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : null;
            })()}

            </Box>

            {/* ── Historical Chart ── */}
            {chartData.length > 1 && (
              <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Timeline sx={{ fontSize: 16, color: '#1e293b' }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b' }}>
                    {t('mf.drawer.scoreHistory')}
                  </Typography>
                </Box>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradScore" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.15} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} width={30} />
                    <RTooltip
                      contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }}
                      formatter={(v, name) => [fmtScore(v), name === 'score' ? t('mf.chart.mfScore') : name === 'heat' ? t('mf.chart.entryHeat') : t('mf.chart.adjusted')]}
                    />
                    <ReferenceLine y={75} stroke="#10b981" strokeDasharray="3 3" strokeOpacity={0.5} />
                    <ReferenceLine y={50} stroke="#f59e0b" strokeDasharray="3 3" strokeOpacity={0.5} />
                    <Area type="monotone" dataKey="score" stroke="#10b981" fill="url(#gradScore)" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="adjusted" stroke="#3b82f6" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
                    <Line type="monotone" dataKey="heat" stroke="#ef4444" strokeWidth={1} dot={false} strokeDasharray="2 2" />
                  </AreaChart>
                </ResponsiveContainer>
                <Box sx={{ display: 'flex', gap: 2, mt: 1, justifyContent: 'center' }}>
                  {[
                    { label: t('mf.chart.mfScore'), color: '#10b981' },
                    { label: t('mf.chart.adjusted'), color: '#3b82f6' },
                    { label: t('mf.chart.entryHeat'), color: '#ef4444' },
                  ].map(l => (
                    <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 3, borderRadius: 1, bgcolor: l.color }} />
                      <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>{l.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
      </Box>
    </Drawer>
  );
};


// ══════════════════════════════════════════════════════════
// MAIN: MoneyFlow PAGE
// ══════════════════════════════════════════════════════════
export default function MoneyFlow() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';

  const [loading, setLoading] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [summary, setSummary] = useState(null);
  const [style, setStyle] = useState('swing');
  const [signalFilter, setSignalFilter] = useState('all');
  const [sortField, setSortField] = useState('adjusted_score');
  const [sortDir, setSortDir] = useState('desc');
  const [searchTicker, setSearchTicker] = useState('');
  const [lookupTicker, setLookupTicker] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  // Ticker dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTicker, setDialogTicker] = useState(null);
  const [dialogInitialData, setDialogInitialData] = useState(null);
  const [dialogCompanyName, setDialogCompanyName] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(true);

  const showSnack = useCallback((msg, severity = 'success') => {
    setSnack({ open: true, msg, severity });
  }, []);

  const openTickerDrawer = useCallback((ticker, prefetchedData = null, prefetchedCompanyName = null) => {
    setDialogTicker(ticker);
    setDialogInitialData(prefetchedData);
    setDialogCompanyName(prefetchedCompanyName);
    setDialogOpen(true);
  }, []);

  const handleLookupTicker = useCallback(async () => {
    const normalizedTicker = lookupTicker.trim().toUpperCase();
    if (!normalizedTicker) {
      showSnack(t('mf.lookup.empty'), 'warning');
      return;
    }

    setLookupLoading(true);
    try {
      const response = await moneyFlowAPI.getTicker(normalizedTicker, { style });
      const payload = response.data;
      const history = payload?.data || [];

      if (history.length === 0) {
        showSnack(t('mf.lookup.noData', { ticker: normalizedTicker }), 'warning');
        return;
      }

      openTickerDrawer(normalizedTicker, payload, payload?.companyName || payload?.company_name || null);
    } catch (error) {
      showSnack(t('mf.lookup.failed', { ticker: normalizedTicker }), 'error');
    } finally {
      setLookupLoading(false);
    }
  }, [lookupTicker, openTickerDrawer, showSnack, style, t]);

  // ── Fetch ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [scanRes, summaryRes] = await Promise.all([
        moneyFlowAPI.scan({ style, top_n: 100, persist: false }).then(r => r.data).catch(() => null),
        moneyFlowAPI.getSummary().then(r => r.data?.data || r.data).catch(() => null),
      ]);
      setScanResult(scanRes);
      setSummary(summaryRes);
    } catch (err) {
      showSnack(t('mf.errorLoading'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnack, style, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Derived data ──
  const results = scanResult?.results || [];
  const filteredResults = results
    .filter(r => {
      if (signalFilter === 'all') return true;
      return _isTier(r, signalFilter);
    })
    .filter(r => {
      if (!searchTicker) return true;
      return (r.ticker || '').toLowerCase().includes(searchTicker.toLowerCase());
    })
    .sort((a, b) => {
      const getVal = (row) => {
        if (sortField === 'adjusted_score') return row.AdjustedScore ?? row.adjusted_score ?? row.MoneyFlowScore ?? row.score ?? 0;
        if (sortField === 'score') return row.MoneyFlowScore ?? row.score ?? 0;
        if (sortField === 'entry_heat_score') return row.entry_heat_score ?? row.EntryRiskScore ?? row.risk_score ?? 0;
        if (sortField === 'stretch_ma20') return row.stretch_ma20 ?? 0;
        if (sortField === 'rsi14') return row.rsi14 ?? 0;
        if (sortField === 'rvol20') return row.rvol20 ?? 0;
        if (sortField === 'consecutive_up') return row.consecutive_up ?? 0;
        if (sortField === 'early_signal_score') return row.EarlySignalScore ?? row.early_signal_score ?? 0;
        return 0;
      };
      const diff = getVal(b) - getVal(a);
      return sortDir === 'desc' ? diff : -diff;
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'desc'
      ? <ArrowDownward sx={{ fontSize: 12, ml: 0.3 }} />
      : <ArrowUpward sx={{ fontSize: 12, ml: 0.3 }} />;
  };

  // ── Summary stat values ──
  const totalScanned = scanResult?.total_scanned || summary?.total || 0;
  const safeCount = results.filter(r => _isTier(r, 'buy_safe')).length;
  const aggressiveCount = results.filter(r => _isTier(r, 'buy_aggressive')).length;
  const accumCount = results.filter(r => _isTier(r, 'watch_accumulating')).length;
  const emergingCount = results.filter(r => _isTier(r, 'emerging')).length;
  const avgScore = summary?.avg_score || (results.length > 0 ? results.reduce((s, r) => s + (r.MoneyFlowScore ?? r.score ?? 0), 0) / results.length : 0);
  const avgHeat = results.length > 0
    ? results.reduce((s, r) => s + (r.entry_heat_score ?? r.EntryRiskScore ?? r.risk_score ?? 0), 0) / results.length
    : 0;

  // ── Build concise recommendations (from dedicated API) ──
  const CONVICTION_TIER_DISPLAY = {
    strong_buy:  { status: t('mf.conviction.strongBuy'),  Icon: CheckCircle,  color: '#10b981', tip: t('mf.conviction.strongBuyTip') },
    buy:         { status: t('mf.conviction.buy'),        Icon: TrendingUp,   color: '#3b82f6', tip: t('mf.conviction.buyTip') },
    accumulate:  { status: t('mf.conviction.accumulate'), Icon: Schedule,     color: '#f59e0b', tip: t('mf.conviction.accumulateTip') },
    watch:       { status: t('mf.conviction.watch'),      Icon: Visibility,   color: '#8b5cf6', tip: t('mf.conviction.watchTip') },
  };
  const CONVICTION_TIERS = Object.entries(CONVICTION_TIER_DISPLAY).map(([tier, meta]) => ({ tier, ...meta }));
  const [recoData, setRecoData] = useState(null);
  const [recoLoading, setRecoLoading] = useState(false);
  const [exportingRecoImage, setExportingRecoImage] = useState(false);
  const recommendationsExportRef = useRef(null);

  const fetchRecommendations = useCallback(async () => {
    setRecoLoading(true);
    try {
      const res = await moneyFlowAPI.getRecommendations({ style });
      setRecoData(res.data);
    } catch (err) {
      setRecoData(null);
    } finally {
      setRecoLoading(false);
    }
  }, [style]);

  useEffect(() => { fetchRecommendations(); }, [fetchRecommendations]);

  const handleExportRecommendationsImage = useCallback(async () => {
    if (!recommendationsExportRef.current || exportingRecoImage || !(recoData?.tickers || []).length) {
      return;
    }

    try {
      setExportingRecoImage(true);

      const canvas = await html2canvas(recommendationsExportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
      });

      const link = document.createElement('a');
      const asOfDate = scanResult?.scan_date?.split(' ')[0] || new Date().toISOString().slice(0, 10);
      link.download = `money-flow-recommendations-${style}-${asOfDate}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Failed to export money flow recommendations image', error);
      showSnack(t('mf.export.failed', 'Không thể xuất ảnh'), 'error');
    } finally {
      setExportingRecoImage(false);
    }
  }, [exportingRecoImage, recoData, scanResult?.scan_date, showSnack, style, t]);

  const mapRecommendationRows = (rows = []) => rows.map((r) => {
    const display = CONVICTION_TIER_DISPLAY[r.conviction_tier] || CONVICTION_TIER_DISPLAY.watch;
    const reason = r.conviction_reason;
    const intentSummary = r.flow_intent_summary;
    const tip = (intentSummary && typeof intentSummary === 'object')
      ? (intentSummary[lang] || intentSummary.vi || intentSummary.en)
      : ((reason && typeof reason === 'object') ? (reason[lang] || reason.vi || reason.en) : reason);
    return { ...r, _status: display.status, _statusIcon: display.Icon, _statusColor: display.color, _statusTip: tip || display.tip, _tier: r.conviction_tier };
  });

  const actionableSection = recoData?.sections?.actionable_entries || null;
  const setupSection = recoData?.sections?.setup_watchlist || null;
  const formationSection = recoData?.sections?.formation_watchlist || null;
  const recommendations = mapRecommendationRows(actionableSection?.tickers || recoData?.tickers || []);
  const setupWatchlist = mapRecommendationRows(setupSection?.tickers || []);
  const formationWatchlist = mapRecommendationRows(formationSection?.tickers || []);
  const actionableTitle = localizeCopy(actionableSection?.title, lang) || t('mf.table.recommendations');
  const actionableSummary = localizeCopy(actionableSection?.summary, lang);
  const setupTitle = localizeCopy(setupSection?.title, lang) || t('mf.setup.title', 'Setup đang xây nền');
  const setupSummary = localizeCopy(setupSection?.summary, lang) || t('mf.setup.summary', 'Danh sách này là các setup đang hình thành, chưa nên đọc như khuyến nghị giải ngân chính.');
  const formationTitle = localizeCopy(formationSection?.title, lang) || t('mf.formation.title', 'Radar hình thành sớm');
  const formationSummary = localizeCopy(formationSection?.summary, lang) || t('mf.formation.summary', 'Nhóm này dùng để theo dõi chủ động các mã đang hình thành nhưng chưa đủ chuẩn đầu tư.');

  return (
    <Box sx={{ p: 2.5, maxWidth: 1480, mx: 'auto' }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 2.5,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(16,185,129,0.25)',
          }}>
            <Waves sx={{ fontSize: 26, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {t('mf.title')}
              </Typography>
              {scanResult?.scan_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981', boxShadow: '0 0 6px #10b981' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>{scanResult.scan_date?.split(' ')[0]}</Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('mf.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={style} exclusive size="small"
            onChange={(_, v) => v && setStyle(v)}
            sx={{
              bgcolor: '#f1f5f9', borderRadius: '6px', border: 'none',
              '& .MuiToggleButton-root': {
                px: 2, py: 0.5, fontSize: 13, fontWeight: 600, textTransform: 'none',
                border: 'none', borderRadius: '6px !important', color: '#64748b',
                '&.Mui-selected': { bgcolor: '#fff', color: '#1e293b', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
                '&.Mui-selected:hover': { bgcolor: '#fff' },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.5)' },
              },
            }}
          >
            <ToggleButton value="swing">Swing</ToggleButton>
            <ToggleButton value="tplus">T+</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh sx={{ fontSize: 16 }} />}
            onClick={fetchData} disabled={loading}
            sx={{
              textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '6px',
              color: '#059669', borderColor: '#6ee7b7', px: 2,
              '&:hover': { bgcolor: '#ecfdf5', borderColor: '#10b981' },
              '&:disabled': { bgcolor: '#f1f5f9', color: '#94a3b8', borderColor: '#e2e8f0' },
            }}
          >
            {loading ? t('mf.scanning') : t('mf.rescan')}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          mb: 2.5,
          p: 1.5,
          borderRadius: 2,
          border: '1px solid #dbeafe',
          bgcolor: '#f8fbff',
          display: 'flex',
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#0f172a', mb: 0.35 }}>
            {t('mf.lookup.title')}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.5 }}>
            {t('mf.lookup.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}>
          <TextField
            size="small"
            placeholder={t('mf.lookup.placeholder')}
            value={lookupTicker}
            onChange={(e) => setLookupTicker(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleLookupTicker();
              }
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: '#93c5fd' }} /></InputAdornment>,
            }}
            sx={{
              width: { xs: '100%', sm: 240 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.8rem',
                bgcolor: '#fff',
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleLookupTicker}
            disabled={lookupLoading}
            startIcon={lookupLoading ? <CircularProgress size={16} color="inherit" /> : <Timeline sx={{ fontSize: 16 }} />}
            sx={{
              minWidth: 140,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '0.78rem',
              fontWeight: 700,
              bgcolor: '#2563eb',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#1d4ed8', boxShadow: 'none' },
            }}
          >
            {lookupLoading ? t('mf.lookup.checking') : t('mf.lookup.action')}
          </Button>
        </Box>
      </Box>

      {/* ── Stat Tiles ── */}
      {loading && !scanResult ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1.5, mb: 2.5 }}>
          {[0,1,2,3,4,5].map(i => (
            <Box key={i} sx={{ borderRadius: 1.5, border: '1px solid #f1f5f9', bgcolor: '#fff', p: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 2, flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="50%" height={14} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="40%" height={24} sx={{ mb: 0.3 }} />
                <Skeleton variant="text" width="65%" height={12} />
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1.5, mb: 2.5 }}>
          <Tile Icon={Assessment} label={t('mf.tiles.scanned')} value={fmt(totalScanned)} sub={t('mf.tiles.shown', { count: filteredResults.length })} color="#64748b" />
          <Tile Icon={CheckCircle} label={t('mf.tiles.safeBuy')} value={fmt(safeCount)} sub={t('mf.tiles.safeBuySub')} color="#059669" />
          <Tile Icon={TrendingUp} label={t('mf.tiles.aggressive')} value={fmt(aggressiveCount)} sub={t('mf.tiles.aggressiveSub')} color="#f97316" />
          <Tile Icon={WaterDrop} label={t('mf.tiles.accumulating')} value={fmt(accumCount)} sub={t('mf.tiles.accumulatingSub')} color="#06b6d4" />
          <Tile Icon={Speed} label={t('mf.tiles.emerging')} value={fmt(emergingCount)} sub={t('mf.tiles.emergingSub')} color="#a855f7" />
          <Tile Icon={Shield} label={t('mf.tiles.avgHeat')} value={fmtScore(avgHeat)} sub={t('mf.tiles.avgScoreSub', { score: fmtScore(avgScore) })} color="#f59e0b" />
        </Box>
      )}

      {/* ══ RECOMMENDATIONS — Curated Quality Picks ══ */}
      {recoLoading && !recoData ? (
        /* Recommendations Skeleton */
        <Box sx={{ mb: 2.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width={160} height={18} />
              <Skeleton variant="rounded" width={24} height={18} sx={{ borderRadius: 3 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {[0,1,2].map(i => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <Skeleton variant="circular" width={13} height={13} />
                  <Skeleton variant="text" width={70} height={14} />
                </Box>
              ))}
            </Box>
          </Box>
          {/* Skeleton table header */}
          <Box sx={{ display: 'flex', gap: 1, px: 2, py: 1, bgcolor: '#fafbfc', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
            {[90, 60, 70, 100, 50, 60, 50, 60, 40, 36, 110].map((w, i) => (
              <Skeleton key={i} variant="text" width={w} height={14} sx={{ flexShrink: 0 }} />
            ))}
          </Box>
          {/* Skeleton rows */}
          {Array.from({ length: 6 }).map((_, ri) => (
            <Box key={ri} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: '1px solid #f8fafc', borderLeft: 3, borderLeftColor: ri < 2 ? '#10b981' : ri < 4 ? '#3b82f6' : '#f59e0b' }}>
              <Skeleton variant="rounded" width={72} height={22} sx={{ borderRadius: 3, flexShrink: 0 }} />
              <Skeleton variant="text" width={48} height={18} sx={{ flexShrink: 0 }} />
              <Skeleton variant="text" width={36} height={18} sx={{ flexShrink: 0 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 80 }}>
                <Skeleton variant="rounded" width="70%" height={6} sx={{ borderRadius: 1 }} />
                <Skeleton variant="text" width={30} height={14} />
              </Box>
              <Skeleton variant="text" width={36} height={14} sx={{ flexShrink: 0 }} />
              <Skeleton variant="text" width={50} height={14} sx={{ flexShrink: 0 }} />
              <Skeleton variant="text" width={50} height={14} sx={{ flexShrink: 0 }} />
              <Skeleton variant="text" width={50} height={14} sx={{ flexShrink: 0 }} />
              <Skeleton variant="text" width={32} height={14} sx={{ flexShrink: 0 }} />
              <Skeleton variant="text" width={28} height={14} sx={{ flexShrink: 0 }} />
              <Box sx={{ display: 'flex', gap: 0.3, flexShrink: 0 }}>
                <Skeleton variant="rounded" width={52} height={18} sx={{ borderRadius: 3 }} />
                <Skeleton variant="rounded" width={44} height={18} sx={{ borderRadius: 3 }} />
              </Box>
            </Box>
          ))}
        </Box>
      ) : recommendations.length > 0 && (
        <Box ref={recommendationsExportRef} sx={{ mb: 2.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          {/* Header */}
          <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.25, mb: 0.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0, flexWrap: 'wrap' }}>
                <Recommend sx={{ fontSize: 16, color: '#10b981' }} />
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#1e293b' }}>
                  {actionableTitle}
                </Typography>
                <Chip label={recommendations.length} size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha('#10b981', 0.08), color: '#10b981' }} />
              </Box>
              <Box data-html2canvas-ignore="true">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={exportingRecoImage ? <CircularProgress size={14} color="inherit" /> : <FileDownloadOutlined sx={{ fontSize: 16 }} />}
                  onClick={handleExportRecommendationsImage}
                  disabled={exportingRecoImage || recommendations.length === 0}
                  sx={{
                    minWidth: 132,
                    textTransform: 'none',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    borderRadius: 2,
                    color: '#475569',
                    borderColor: '#cbd5e1',
                    bgcolor: '#fff',
                    '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
                  }}
                >
                  {exportingRecoImage
                    ? t('mf.export.exportingImage', 'Đang xuất ảnh...')
                    : t('common.exportImage', 'Xuất ảnh')}
                </Button>
              </Box>
            </Box>
            {actionableSummary && (
              <Typography sx={{ fontSize: '0.68rem', color: '#64748b', lineHeight: 1.5, mb: 0.75 }}>
                {actionableSummary}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
              {CONVICTION_TIERS.map(g => {
                const count = recommendations.filter(r => r._tier === g.tier).length;
                if (count === 0) return null;
                return (
                  <Box key={g.tier} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <g.Icon sx={{ fontSize: 13, color: g.color }} />
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: g.color }}>{count} {g.status}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...thSx, width: 90 }}>{t('mf.table.status')}</TableCell>
                  <TableCell sx={thSx}>{t('mf.table.ticker')}</TableCell>
                  <TableCell sx={{ ...thSx, width: 86 }}>{t('mf.conviction.label')}</TableCell>
                  <TableCell sx={thSx}>{t('mf.table.flowScore')}</TableCell>
                  <TableCell sx={thSx}>{t('mf.table.entryHeat')}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right' }}>{t('mf.table.price')}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right' }}>{t('mf.table.entry')}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right' }}>{t('mf.table.stopLoss')}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right' }}>{t('mf.table.targetPrice')}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'center' }}>{t('mf.table.rrFriendly')}</TableCell>
                  <TableCell sx={thSx}>RVol</TableCell>
                  <TableCell sx={thSx}>RSI</TableCell>
                  <TableCell sx={thSx}>{t('mf.table.catalysts')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recommendations.map((row, idx) => {
                  const ticker = row.ticker;
                  const heatScore = row.entry_heat_score ?? row.entry_risk;
                  const conviction = row.conviction_score;
                  const rvol = row.rvol;
                  const rsi = row.rsi14;
                  const entryLevel = row.entry_break_level;
                  const stopLevel = row.stop_loss_level;
                  const targetLevel = row.target_price;
                  const upsidePct = row.upside_pct;
                  const riskReward = row.risk_reward;
                  const StatusIcon = row._statusIcon;

                  // Catalyst chips from API
                  const CATALYST_META = {
                    bb_squeeze:     { label: t('mf.signal.bbSqueeze'),  color: '#a855f7' },
                    vol_spring:     { label: t('mf.signal.volSpring'),  color: '#06b6d4' },
                    bullish_div:    { label: t('mf.table.bullishDivShort'), color: '#10b981' },
                    pocket_pivot:   { label: t('mf.signal.pocketPivot'), color: '#3b82f6' },
                    flow_accel:     { label: t('mf.signal.flowAccelShort', { defaultValue: 'Accel' }), color: '#a855f7' },
                    breakout:       { label: t('mf.signal.breakout'),   color: '#f59e0b' },
                    roc_divergence: { label: t('mf.signal.rocDiv', { defaultValue: 'ROC Div' }), color: '#3b82f6' },
                  };
                  const catalysts = (row.catalysts || []).map(c => CATALYST_META[c] || { label: c, color: '#94a3b8' });

                  return (
                    <TableRow
                      key={`reco-${ticker}`}
                      hover
                      sx={{
                        cursor: 'pointer',
                        borderLeft: 3, borderLeftColor: row._statusColor,
                        '&:hover': { bgcolor: alpha(row._statusColor, 0.04) },
                      }}
                      onClick={() => openTickerDrawer(
                        ticker,
                        buildDrawerSnapshotPayload(ticker, style, row, row.company_name || row.companyName || null),
                        row.company_name || row.companyName || null,
                      )}
                    >
                      <TableCell sx={tdSx}>
                        <Tooltip title={row._statusTip} arrow placement="right">
                          <Chip
                            icon={<StatusIcon sx={{ fontSize: '13px !important', color: `${row._statusColor} !important` }} />}
                            label={row._status}
                            size="small"
                            sx={{
                              height: 22, fontSize: '0.65rem', fontWeight: 700,
                              bgcolor: alpha(row._statusColor, 0.08), color: row._statusColor,
                              border: `1px solid ${alpha(row._statusColor, 0.2)}`,
                              cursor: 'help',
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#0f172a' }}>{ticker}</Typography>
                        {!!row.setup_diagnostics && (
                          <Typography sx={{ fontSize: '0.58rem', color: '#64748b', mt: 0.15, lineHeight: 1.45 }}>
                            {buildSetupDiagnosticSummary(row, t)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Tooltip title={`Conviction: ${conviction?.toFixed(1) || '—'}/100`} arrow>
                          <Box component="span">
                            <ScoreBadge display={row.conviction_display} tooltipLabel={t('mf.conviction.label')} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <ScoreBadge display={row.mf_score_display} tooltipLabel={t('mf.table.flowScore')} />
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <EntryHeatBadge
                          level={row.entry_heat_level}
                          label={row.entry_heat_label}
                          reason={row.entry_heat_reason}
                          score={heatScore}
                        />
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '0.78rem', fontFamily: 'monospace', color: '#0f172a', fontWeight: 600 }}>
                          {fmt(row.close)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '0.78rem', fontFamily: 'monospace', color: '#3b82f6', fontWeight: 700 }}>
                          {entryLevel ? fmt(entryLevel) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '0.78rem', fontFamily: 'monospace', color: '#ef4444', fontWeight: 700 }}>
                          {stopLevel ? fmt(stopLevel) : '—'}
                        </Typography>
                        {entryLevel && stopLevel && (
                          <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                            {((entryLevel - stopLevel) / entryLevel * 100).toFixed(1)}%
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '0.78rem', fontFamily: 'monospace', color: '#10b981', fontWeight: 700 }}>
                          {targetLevel ? fmt(targetLevel) : '—'}
                        </Typography>
                        {upsidePct != null && (
                          <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                            +{upsidePct.toFixed(1)}%
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                        <Tooltip
                          title={
                            upsidePct != null && row.downside_pct != null
                              ? `${t('mf.table.targetPrice')}: ${fmt(targetLevel)} | ${t('mf.table.stopLoss')}: ${fmt(stopLevel)}`
                              : t('mf.table.noTargetData')
                          }
                          arrow
                        >
                          <Typography sx={{
                            fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace',
                            color: row.risk_reward_display?.color || '#94a3b8',
                          }}>
                            {riskReward != null ? `${riskReward.toFixed(1)}x` : '—'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Typography sx={{
                          fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace',
                          color: row.rvol_display?.color || '#94a3b8',
                        }}>
                          {rvol != null ? `${rvol.toFixed(1)}x` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Typography sx={{
                          fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace',
                          color: row.rsi14_display?.color || '#64748b',
                        }}>
                          {fmtScore(rsi)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
                          {catalysts.length > 0 ? catalysts.map(s => (
                            <Chip key={s.label} label={s.label} size="small" sx={{ fontSize: '0.55rem', height: 18, bgcolor: alpha(s.color, 0.1), color: s.color, fontWeight: 700 }} />
                          )) : (
                            <Typography sx={{ fontSize: '0.7rem', color: '#cbd5e1' }}>—</Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <WatchlistTableSection
        icon={Timeline}
        accentColor="#2563eb"
        title={setupTitle}
        summary={setupSummary}
        rows={setupWatchlist}
        lang={lang}
        style={style}
        openTickerDrawer={openTickerDrawer}
        t={t}
      />

      <WatchlistTableSection
        icon={Visibility}
        accentColor="#d97706"
        title={formationTitle}
        summary={formationSummary}
        rows={formationWatchlist}
        lang={lang}
        style={style}
        openTickerDrawer={openTickerDrawer}
        t={t}
      />

      {/* ══ FULL LEADERBOARD — Collapsible ══ */}
      {loading && !scanResult ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Skeleton variant="circular" width={16} height={16} />
            <Skeleton variant="text" width={150} height={18} />
            <Skeleton variant="rounded" width={28} height={18} sx={{ borderRadius: 3 }} />
          </Box>
          <Skeleton variant="circular" width={20} height={20} />
        </Box>
      ) : (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, cursor: 'pointer' }}
        onClick={() => setShowLeaderboard(prev => !prev)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Assessment sx={{ fontSize: 16, color: '#64748b' }} />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#475569' }}>
            {t('mf.table.fullLeaderboard')}
          </Typography>
          <Chip label={results.length} size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha('#64748b', 0.08), color: '#64748b' }} />
        </Box>
        {showLeaderboard ? <ExpandLess sx={{ fontSize: 20, color: '#94a3b8' }} /> : <ExpandMore sx={{ fontSize: 20, color: '#94a3b8' }} />}
      </Box>
      )}

      <Collapse in={showLeaderboard}>
      {/* ── Signal Filter Tabs + Search ── */}
      {loading && !scanResult ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[72, 78, 84, 90, 72, 70, 60].map((w, i) => (
              <Skeleton key={i} variant="rounded" width={w} height={30} sx={{ borderRadius: 2 }} />
            ))}
          </Box>
          <Skeleton variant="rounded" width={180} height={34} sx={{ borderRadius: 2 }} />
        </Box>
      ) : (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {SIGNAL_FILTERS.map(f => {
            const count = f.key === 'all' ? results.length
              : results.filter(r => _isTier(r, f.key)).length;
            const active = signalFilter === f.key;
            const Ic = f.icon;
            return (
              <Button
                key={f.key}
                size="small"
                startIcon={<Ic sx={{ fontSize: 14 }} />}
                onClick={() => setSignalFilter(f.key)}
                sx={{
                  px: 1.5, py: 0.5, fontSize: '0.72rem', fontWeight: 700, textTransform: 'none',
                  borderRadius: 2,
                  bgcolor: active ? alpha(f.color, 0.1) : 'transparent',
                  color: active ? f.color : '#94a3b8',
                  border: active ? `1.5px solid ${alpha(f.color, 0.3)}` : '1.5px solid transparent',
                  '&:hover': { bgcolor: alpha(f.color, 0.08) },
                }}
              >
                {t(f.labelKey)} ({count})
              </Button>
            );
          })}
        </Box>
        <TextField
          size="small" placeholder={t('mf.table.searchTicker')}
          value={searchTicker} onChange={e => setSearchTicker(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: '#cbd5e1' }} /></InputAdornment>,
          }}
          sx={{ width: 180, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.78rem' } }}
        />
      </Box>
      )}

      {/* ── Leaderboard Table ── */}
      {loading && !scanResult ? (
        <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          {/* skeleton header */}
          <Box sx={{ display: 'flex', gap: 1, px: 2, py: 1.2, bgcolor: '#fafbfc', borderBottom: '1px solid #e2e8f0' }}>
            <Skeleton sx={{ flexShrink: 0, width: 40 }} height={14} />
            <Skeleton sx={{ flexShrink: 0, width: 70 }} height={14} />
            <Skeleton sx={{ flexShrink: 0, width: 80 }} height={14} />
            <Skeleton sx={{ flexShrink: 0, width: 80 }} height={14} />
            <Skeleton sx={{ flexShrink: 0, width: 72 }} height={14} />
            <Skeleton sx={{ flexShrink: 0, width: 80 }} height={14} />
            <Skeleton sx={{ flex: 1, minWidth: 60 }} height={14} />
            <Skeleton sx={{ flexShrink: 0, width: 60 }} height={14} />
            <Skeleton sx={{ flexShrink: 0, width: 72 }} height={14} />
            <Skeleton sx={{ flexShrink: 0, width: 50 }} height={14} />
            <Skeleton sx={{ flexShrink: 0, width: 56 }} height={14} />
            <Skeleton sx={{ flexShrink: 0, width: 64 }} height={14} />
            <Skeleton sx={{ flexShrink: 0, width: 32 }} height={14} />
          </Box>
          {/* skeleton rows */}
          {Array.from({ length: 10 }).map((_, ri) => (
            <Box key={ri} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: '1px solid #f8fafc' }}>
              <Skeleton sx={{ flexShrink: 0, width: 20 }} height={16} />
              <Skeleton sx={{ flexShrink: 0, width: 52 }} height={18} />
              <Skeleton variant="rounded" sx={{ flexShrink: 0, width: 80 }} height={8} />
              <Skeleton sx={{ flexShrink: 0, width: 36 }} height={16} />
              <Skeleton variant="circular" sx={{ flexShrink: 0, width: 28, height: 28 }} />
              <Skeleton variant="rounded" sx={{ flexShrink: 0, width: 68 }} height={20} />
              <Skeleton sx={{ flex: 1, minWidth: 36 }} height={16} />
              <Skeleton sx={{ flexShrink: 0, width: 36 }} height={16} />
              <Skeleton sx={{ flexShrink: 0, width: 44 }} height={16} />
              <Skeleton sx={{ flexShrink: 0, width: 32 }} height={16} />
              <Skeleton sx={{ flexShrink: 0, width: 24 }} height={16} />
              <Skeleton sx={{ flexShrink: 0, width: 48 }} height={14} />
              <Skeleton variant="circular" sx={{ flexShrink: 0, width: 24, height: 24, ml: 'auto' }} />
            </Box>
          ))}
        </Box>
      ) : filteredResults.length === 0 ? (
        <EmptyState Icon={Waves} title={t('mf.table.noResults')} subtitle={t('mf.table.noResultsSub')} />
      ) : (
        <TableContainer sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thSx, width: 40, textAlign: 'center' }}>#</TableCell>
                <TableCell sx={{ ...thSx, width: 70 }}>{t('mf.table.ticker')}</TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: '#475569' } }} onClick={() => handleSort('adjusted_score')}>
                  {t('mf.table.adjusted')}<SortIcon field="adjusted_score" />
                </TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: '#475569' } }} onClick={() => handleSort('score')}>
                  {t('mf.table.mfScore')}<SortIcon field="score" />
                </TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: '#475569' } }} onClick={() => handleSort('entry_heat_score')}>
                  {t('mf.table.entryHeat')}<SortIcon field="entry_heat_score" />
                </TableCell>
                <TableCell sx={thSx}>{t('mf.table.signal')}</TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: '#475569' } }} onClick={() => handleSort('early_signal_score')}>
                  {t('mf.table.early')}<SortIcon field="early_signal_score" />
                </TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: '#475569' } }} onClick={() => handleSort('rvol20')}>
                  {t('mf.table.rvol')}<SortIcon field="rvol20" />
                </TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: '#475569' } }} onClick={() => handleSort('stretch_ma20')}>
                  {t('mf.table.stretch20')}<SortIcon field="stretch_ma20" />
                </TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: '#475569' } }} onClick={() => handleSort('rsi14')}>
                  {t('mf.table.rsi')}<SortIcon field="rsi14" />
                </TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: '#475569' } }} onClick={() => handleSort('consecutive_up')}>
                  {t('mf.table.consecutive')}<SortIcon field="consecutive_up" />
                </TableCell>
                <TableCell sx={thSx}>{t('mf.table.value')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredResults.map((row, idx) => {
                const ticker = row.ticker;
                const mfScore = row.MoneyFlowScore ?? row.score;
                const adjScore = row.AdjustedScore ?? row.adjusted_score;
                const heatScore = row.entry_heat_score ?? row.EntryRiskScore ?? row.risk_score;
                const tier = row.signal_tier || 'neutral';
                const tierCfg = TIER_CONFIG[tier] || TIER_CONFIG.neutral;
                const stretch = row.stretch_ma20;
                const rsi = row.rsi14;
                const consec = row.consecutive_up;
                const rvol = row.rvol20;
                const avgVal = row.avg_value_20d;

                return (
                  <TableRow key={ticker} hover
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
                    onClick={() => openTickerDrawer(
                      ticker,
                      buildDrawerSnapshotPayload(ticker, style, row, row.company_name || row.companyName || null),
                      row.company_name || row.companyName || null,
                    )}
                  >
                    <TableCell sx={{ ...tdSx, textAlign: 'center', fontWeight: 700, color: '#94a3b8', fontSize: '0.72rem' }}>
                      {idx + 1}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#1e293b' }}>{ticker}</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}><ScoreBadge score={adjScore} tooltipLabel={t('mf.table.mfScore')} /></TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: scoreColor(mfScore), fontFamily: 'monospace' }}>{fmtScore(mfScore)}</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <EntryHeatBadge
                        level={row.entry_heat_level}
                        label={row.entry_heat_label}
                        reason={row.entry_heat_reason}
                        score={heatScore}
                      />
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: tierCfg.color }}>
                        {t(tierCfg.labelKey)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      {(() => {
                        const es = row.EarlySignalScore ?? row.early_signal_score;
                        return <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: earlyColor(es), fontFamily: 'monospace' }}>{fmtScore(es)}</Typography>;
                      })()}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Tooltip title={t('mf.table.rvolTooltip', { value: rvol != null ? rvol.toFixed(2) : '—' })}>
                        <Typography sx={{
                          fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace',
                          color: rvol >= 2 ? '#10b981' : rvol >= 1.2 ? '#3b82f6' : '#94a3b8',
                        }}>
                          {rvol != null ? `${rvol.toFixed(1)}x` : '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{
                        fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace',
                        color: stretchColor(stretch),
                      }}>
                        {fmtPct(stretch)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{
                        fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace',
                        color: rsi > 80 ? '#ef4444' : rsi > 70 ? '#f59e0b' : rsi < 30 ? '#10b981' : '#64748b',
                      }}>
                        {fmtScore(rsi)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{
                        fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace',
                        color: consec >= 5 ? '#ef4444' : consec >= 3 ? '#f59e0b' : '#64748b',
                      }}>
                        {consec != null ? consec : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontFamily: 'monospace' }}>
                        {fmtBn(avgVal)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      </Collapse>

      {/* ── Ticker Detail Dialog ── */}
      <TickerDrawer
        open={dialogOpen} onClose={() => {
          setDialogOpen(false);
          setDialogInitialData(null);
          setDialogCompanyName(null);
        }}
        ticker={dialogTicker}
        style={style}
        initialData={dialogInitialData ? {
          ...dialogInitialData,
          companyName: dialogInitialData.companyName || dialogInitialData.company_name || dialogCompanyName || null,
        } : (dialogCompanyName ? { companyName: dialogCompanyName } : null)}
      />

      {/* ── Snackbar ── */}
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
